import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Clock,
  UserCheck,
  Activity,
} from 'lucide-react';
import { StatChip, StatTile } from '@/components/ui/StatTile';
import { fetchExtensionStatus, fetchActiveCalls, fetchQueueStatus } from '@/services/api';

interface WidgetData {
  totalExtensions: number;
  idleExtensions: number;
  busyExtensions: number;
  activeCalls: number;
  inboundCalls: number;
  outboundCalls: number;
  waitingCalls: number;
  totalQueues: number;
}

export function LiveDashboardWidgets() {
  const [data, setData] = useState<WidgetData>({
    totalExtensions: 0,
    idleExtensions: 0,
    busyExtensions: 0,
    activeCalls: 0,
    inboundCalls: 0,
    outboundCalls: 0,
    waitingCalls: 0,
    totalQueues: 0,
  });
  const [loading, setLoading] = useState(true);

  const loadInFlight = useRef(false);

  const loadData = useCallback(async () => {
    if (loadInFlight.current) return;
    loadInFlight.current = true;
    try {
      const [extensions, calls, queues] = await Promise.all([
        fetchExtensionStatus(),
        fetchActiveCalls(),
        fetchQueueStatus(),
      ]);

      const idleCount = extensions.filter((e) => e.status === 'idle').length;
      const busyCount = extensions.filter(
        (e) => e.status === 'busy' || e.status === 'ringing'
      ).length;
      const inboundCount = calls.filter((c) => c.call_type === 'Inbound').length;
      const outboundCount = calls.filter((c) => c.call_type === 'Outbound').length;
      const waitingCount = queues.reduce((sum, q) => sum + q.waiting_count, 0);

      setData({
        totalExtensions: extensions.length,
        idleExtensions: idleCount,
        busyExtensions: busyCount,
        activeCalls: calls.length,
        inboundCalls: inboundCount,
        outboundCalls: outboundCount,
        waitingCalls: waitingCount,
        totalQueues: queues.length,
      });
    } catch (error) {
      console.error('Error loading widget data:', error);
    } finally {
      loadInFlight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    // Auto-refresh every 5 seconds
    const interval = setInterval(loadData, 5000);

    return () => clearInterval(interval);
  }, [loadData]);

  const show = (value: number) => (loading ? '–' : value);

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 2xl:grid-cols-6">
      <StatTile
        featured
        label="Active calls"
        value={show(data.activeCalls)}
        icon={Phone}
        caption="In progress right now"
      />
      <StatTile
        label="Inbound"
        value={show(data.inboundCalls)}
        icon={PhoneIncoming}
        caption="Calls coming in"
      />
      <StatTile
        label="Outbound"
        value={show(data.outboundCalls)}
        icon={PhoneOutgoing}
        caption="Calls going out"
      />
      <StatTile
        label="Waiting"
        value={show(data.waitingCalls)}
        icon={Clock}
        dotClassName={data.waitingCalls > 0 ? 'bg-amber-500' : undefined}
        caption={
          <>
            <StatChip>{data.totalQueues}</StatChip>
            {data.totalQueues === 1 ? 'queue' : 'queues'}
          </>
        }
      />
      <StatTile
        label="Idle"
        value={show(data.idleExtensions)}
        icon={UserCheck}
        dotClassName="bg-primary-500"
        caption={
          <>
            <StatChip>{data.totalExtensions}</StatChip>
            extensions in total
          </>
        }
      />
      <StatTile
        label="Busy"
        value={show(data.busyExtensions)}
        icon={Activity}
        dotClassName={data.busyExtensions > 0 ? 'bg-red-500' : undefined}
        caption="Ringing or talking"
      />
    </div>
  );
}
